package com.neurobs.react

import org.scalajs.dom.html.Select
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.{React, ReactRef}
import slinky.core.facade.Hooks._
import slinky.web.html._

import scala.collection.mutable.ArrayBuffer
import scala.scalajs.js

/** Properties for an HTML select option tag
 *
 * @param caption The text of the option
 * @param disabled The disabled state of the option
 */
case class SelectOption
(
	caption: String,
	disabled: Boolean = false
)

/** Used by SelectState to store the selection value */
trait SelectWrapper {
	def selectIndex: Option[Int]
	def setSelectIndex(i: Option[Int]): Unit
}

case class SelectProperties
(
	options: Seq[SelectOption],
	wrapper: SelectWrapper
)

/** Stores state of a single-selection control with options and selection state (mutable)
 *
 * This class manages three main things:
 * 1. (optionally) generating option captions for a list of objects
 * 2. (optionally) generating a value from the selected object
 * 3. Managing optional "Please select..." or "[None]" drown-down items to support "no selection" values
 * and conversion to-from the actual control options and selection index.
 *
 * @note When used with SelectControl, the html control is "unmanaged", and therefore this
 *       class stored the selection value as a mutable value that may change from interactions
 *       with the control.
 *
 * @param items The options that may be selected
 * @param initialSelection Index into the items sequences of the selection, or None if no selection
 * @param noSelectOption If the box should include an extra "Please select..." or "[None]" option
 * @param noneCaption If noSelectOption is true, makes the extra option "[None]" and selectable
 * @param toCaption Generates the text for the option in the control from an item
 * @param toValue Generates a value for the selected item
 * @tparam T Type of options
 * @tparam V Type of value
 */
class SelectState[T, V] private (val items: Seq[T], initialSelection: Option[Int], noSelectOption: Boolean, noneCaption: Boolean, toCaption: T => String, toValue: T => V)(implicit generateWrapper: Option[Int] => SelectWrapper)
{
	val control: SelectWrapper = generateWrapper(toControlIndex(initialSelection))

	/** Generates properties of the actual control */
	def generateProperties(): SelectProperties = {
		val options = {
			val prefixOptions = if (noSelectOption) Seq(SelectOption(
				caption = if (noneCaption) SelectState.noneOptionText else SelectState.pleaseSelectOptionText,
				disabled = !noneCaption
			))
			else Seq()

			val options = items.map(i => SelectOption(caption = toCaption(i)))

			prefixOptions ++ options
		}
		SelectProperties(options, control)
	}

	private def toControlIndex(index: Option[Int]): Option[Int] = index match {
		case None => if (noSelectOption) Some(0) else None
		case Some(i) => if (noSelectOption) Some(i + 1) else Some(i)
	}

	def selectIndex: Option[Int] = {
		val adjusted = control.selectIndex.getOrElse(-1) - (if (noSelectOption) 1 else 0)
		if (adjusted < 0 || adjusted >= items.size) None else Some(adjusted)
	}

	/** The selected value if any */
	def value: Option[V] = selectIndex.map(i => toValue(items(i)))

	def setSelection(newSelectIndex: Option[Int]): Unit = control.setSelectIndex(toControlIndex(newSelectIndex))

	def setSelectionValue(value: Option[T], comp: (T, T) => Boolean): Unit = {
		val index = value flatMap { v =>
			val i = items.indexWhere(comp(_, v))
			if (i == -1) None else Some(i)
		}
		setSelection(index)
	}

	def withNewItems(newItems: Seq[T], initialSelection: Option[Int]): SelectState[T, V] = {
		val sel = initialSelection.flatMap(i => if (i < newItems.size) Some(i) else None)
		new SelectState(items = newItems, initialSelection = sel, noSelectOption = noSelectOption, noneCaption = noneCaption, toCaption = toCaption, toValue = toValue)(generateWrapper)
	}
}

object SelectState
{
	def simple(items: Seq[String], selectIndex: Option[Int], noSelectOption: Boolean = false, noneCaption: Boolean = false)(implicit factory: ControlWrapperFactory) =
		new SelectState[String, String](items = items, initialSelection = selectIndex, noSelectOption = noSelectOption, noneCaption = noneCaption, toCaption = s => s, toValue = v => v)(factory.select(_))

	def simpleWithValue(items: Seq[String], value: Option[String], noSelectOption: Boolean = false, noneCaption: Boolean = false)(implicit factory: ControlWrapperFactory) =
		typed[String](items = items, value = value, noSelectOption = noSelectOption, noneCaption = noneCaption, toCaption = v => v)

	def typed[T](items: Seq[T], value: Option[T], noSelectOption: Boolean = false, noneCaption: Boolean = false, toCaption: T => String)(implicit factory: ControlWrapperFactory) = {
		val selectIndex = value flatMap { v =>
			items.indexOf(v) match {
				case -1 => None
				case i => Some(i)
			}
		}
		new SelectState[T, T](items = items, initialSelection = selectIndex, noSelectOption = noSelectOption, noneCaption = noneCaption, toCaption = toCaption, toValue = v => v)(factory.select(_))
	}

	def apply[T, V](items: Seq[T], selectIndex: Option[Int], noSelectOption: Boolean = false, noneCaption: Boolean = false, toCaption: T => String, toValue: T => V)(implicit factory: ControlWrapperFactory) = new SelectState[T, V](items = items, initialSelection = selectIndex, noSelectOption = noSelectOption, noneCaption = noneCaption, toCaption = toCaption, toValue = toValue)(factory.select(_))

	private val pleaseSelectOptionText = "Please select..."
	private val noneOptionText = "[None]"
}

/** SelectControlWrapper containing a React ref which the React component assigns to
 * the actual select control. It also contains a value cache that is used to store the value
 * if the ref is unattached and then reattached.
 *
 * @param selectionCache Initial selection value, and final value if the control is unattached
 *                       if the control is unmounted.
 * @param ref
 */
class SelectWrapperReact(var selectionCache: Option[Int]) extends SelectWrapper {
	val ref: ReactRef[Select] = React.createRef[Select]

	private def get: Option[Select] = if (ref.current == null) None else Some(ref.current)

	override def selectIndex: Option[Int] = get match {
		case None => selectionCache
		case Some(s) => SelectWrapperReact.toOption(s)
	}

	override def setSelectIndex(i: Option[Int]): Unit = {
		selectionCache = i
		get.foreach(s => s.selectedIndex = i.getOrElse(-1))
	}
}

object SelectWrapperReact {
	def toOption(s: Select): Option[Int] = {
		val i = s.selectedIndex
		if (i < 0) None else Some(i)
	}
}

case class SelectWrapperControlled(var selection: Option[Int]) extends SelectWrapper {
	override def selectIndex: Option[Int] = selection
	override def setSelectIndex(i: Option[Int]): Unit = { selection = i }
}

/** Implementation of SelectControlWrapper that simply contains the selection value
 *
 * @param selection The selection value
 */
case class SelectWrapperTest(var selection: Option[Int]) extends SelectWrapper {
	override def selectIndex: Option[Int] = selection
	override def setSelectIndex(i: Option[Int]): Unit = { selection = i }
}

/** Wrapper around a "option" tag used by SelectControl */
@react object SelectControlOption {
	case class Props(opt: SelectOption, value: String)
	val component = FunctionalComponent[Props] { props =>
		option(
			value := props.value,
			disabled := props.opt.disabled,
			props.opt.caption
		)
	}
}

/** A drop-down select box */
@react object Select {
	/**
	  * @param props Control properties
	  * @param emWidth Optional CSS width in "em" units
	  * @param onSelectionChange Selection change handler taking the selected index
	  */
	case class Props
	(
		props: SelectProperties,
		emWidth: Option[Double] = None,
		onSelectionChange: Option[() => Unit] = None
	)

	val component = FunctionalComponent[Props] { props =>
		val wrapper = props.props.wrapper.asInstanceOf[SelectWrapperReact]
		val initialValue = wrapper.selectionCache.map(_.toString).getOrElse("")
		val styleDict = js.Dynamic.literal()
		props.emWidth.foreach(w => styleDict.width = s"${w}em")

		useEffect( { () =>
			if (wrapper.ref.current != null && initialValue.isEmpty) {
				wrapper.ref.current.selectedIndex = -1
			}
			() => {
				if (wrapper.ref.current != null) {
					wrapper.selectionCache = SelectWrapperReact.toOption(wrapper.ref.current)
				}
			}
		}, List(wrapper.ref))

		select(
			className := defaultSelectClass,
			ref := wrapper.ref,
			style := styleDict,
			onChange := props.onSelectionChange,
			defaultValue := initialValue,
			props.props.options.view.zipWithIndex map { opt =>
				SelectControlOption(opt._1, opt._2.toString).withKey(opt._2.toString)
			}
		)
	}

	private val defaultSelectClass = "efactory-select"
}

/** A drop-down select box that doesn't maintain its own value*/
@react object SelectControlled {
	/**
	 * @param props Control properties
	 * @param emWidth Optional CSS width in "em" units
	 * @param onSelectionChange Selection change handler taking the selected index
	 */
	case class Props
	(
		props: SelectProperties,
		emWidth: Option[Double] = None,
		onSelectionChange: () => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val wrapper = props.props.wrapper.asInstanceOf[SelectWrapperControlled]
		val indexValue = wrapper.selectIndex.map(_.toString).getOrElse("")
		val styleDict = js.Dynamic.literal()
		props.emWidth.foreach(w => styleDict.width = s"${w}em")

		select(
			className := defaultSelectClass,
			style := styleDict,
			onChange := (e => {
				val i = SelectWrapperReact.toOption(e.target.asInstanceOf[Select])
				wrapper.setSelectIndex(i)
				props.onSelectionChange()
			}),
			value := indexValue,
			props.props.options.view.zipWithIndex map { opt =>
				SelectControlOption(opt._1, opt._2.toString).withKey(opt._2.toString)
			}
		)
	}

	private val defaultSelectClass = "efactory-select"
}